import { Truck, Wrench, Shield, IndianRupee, FileText, Settings as Cog, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSettings } from "@/lib/settings";
import { Section } from "@/components/Section";

// Icon name → component map (admin can reference by name in the JSON)
const ICONS = { Truck, Wrench, Shield, IndianRupee, FileText, Settings: Cog, Cog };
const ICON_ROTATION = [Truck, IndianRupee, Wrench, FileText, Shield, Cog];

const DEFAULT_SERVICES = [
  { title: "BUY", desc: "Curated inventory of new & used construction equipment.", points: "New & Used inventory, 87-point inspection, Pan-India delivery" },
  { title: "SELL", desc: "We pay top market value for well-maintained used equipment.", points: "Free site inspection, Same-day quote, Instant payment options" },
  { title: "RENT", desc: "Short and long term rentals — daily, monthly or project-based.", points: "Daily / Monthly / Project, Operator support, Pan-Bihar coverage" },
  { title: "FINANCE", desc: "Best EMI deals from 8+ NBFC & bank partners.", points: "Tie-ups with 8+ lenders, Pre-approval in 48 hrs, Documentation support" },
  { title: "INSURANCE", desc: "Comprehensive equipment insurance with quick claim settlement.", points: "Comprehensive cover, Quick claim settlement, Annual renewals managed" },
  { title: "SERVICE & AMC", desc: "Authorised Bull Machines service across Bihar.", points: "On-site service team, Genuine parts, AMC packages available" },
];

// Render service hero title with last word highlighted in saffron
function renderHero(title) {
  const parts = title.split(".");
  const last = [...parts].reverse().find((p) => p.trim());
  return parts.map((seg, i) => {
    if (!seg.trim()) return <span key={i}>{i < parts.length - 1 ? "." : ""}</span>;
    return <span key={i} className={seg === last ? "text-[#E65C00]" : ""}>{seg}{i < parts.length - 1 ? "." : ""}</span>;
  });
}

export default function Services() {
  const { settings } = useSettings();
  const items = (settings?.services_items && settings.services_items.length > 0) ? settings.services_items : DEFAULT_SERVICES;

  return (
    <>
      <section className="bg-[#1A1A1A] text-white py-24 noise-overlay" data-testid="services-hero">
        <Section>
          <div className="label-eyebrow mb-4">{settings?.services_eyebrow || "Services"}</div>
          <h1 className="heading-xl text-5xl sm:text-6xl lg:text-7xl">
            {renderHero(settings?.services_hero_title || "EVERY SOLUTION. ONE ROOF.")}
          </h1>
          <p className="mt-6 text-gray-300 max-w-2xl">
            {settings?.services_hero_subtitle || "From financing to after-sales, we handle every link in your equipment value chain — so you focus on the work, not the paperwork."}
          </p>
        </Section>
      </section>

      <Section className="py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
          {items.map((s, i) => {
            const Icon = (s.icon && ICONS[s.icon]) ? ICONS[s.icon] : ICON_ROTATION[i % ICON_ROTATION.length];
            const pointsList = (typeof s.points === "string")
              ? s.points.split(",").map((x) => x.trim()).filter(Boolean)
              : (Array.isArray(s.points) ? s.points : []);
            return (
              <div key={`${s.title}-${i}`} className="bg-white p-8 group hover:bg-[#1A1A1A] transition-colors duration-300" data-testid={`services-card-${i}`}>
                <Icon className="w-10 h-10 text-[#E65C00]" />
                <h3 className="heading-xl text-2xl mt-6 group-hover:text-white">{s.title}</h3>
                <p className="text-gray-700 group-hover:text-gray-300 mt-3 text-sm leading-relaxed">{s.desc}</p>
                {pointsList.length > 0 && (
                  <ul className="mt-5 space-y-1.5">
                    {pointsList.map((pt) => (
                      <li key={pt} className="text-xs text-gray-600 group-hover:text-gray-400 flex items-center gap-2">
                        <span className="w-1 h-1 bg-[#E65C00]" /> {pt}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <section className="bg-[#FFCC00] py-16" data-testid="services-cta-band">
        <Section>
          <div className="grid md:grid-cols-2 items-center gap-8">
            <h2 className="heading-xl text-3xl sm:text-4xl text-[#1A1A1A]">
              {settings?.services_cta_title || "NEED A CUSTOM SOLUTION? CALL US."}
            </h2>
            <div className="md:text-right">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white font-heading font-black uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-black transition-colors" data-testid="services-cta">
                {settings?.services_cta_button || "Talk to an Expert"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Section>
      </section>
    </>
  );
}
